import React, { FunctionComponent } from 'react'
import { FormattedMessage } from 'react-intl'

import styled, { useTheme } from 'styled-components/native'

import { BodyText } from '../../atoms/bodyText/BodyText'
import { LoadingIndicator } from '../../atoms/icons/LoadingIndicator'
import { BodyTextSize } from '../../styles/typeStyles'
import { tID } from '../../utils/utils'
import { ProviderAgendaItem, ProviderAgendaItemProps } from '../providerAgendaItem/ProviderAgendaItem'

export type ProviderAgendaPanelProps = {
  appointments: ProviderAgendaItemProps[]
  isLoading: boolean
  allCompleted?: boolean
}

const Container = styled.View({
  flexShrink: 1,
})

const CenteredState = styled.View(({ theme }) => ({
  alignItems: 'center',
  justifyContent: 'center',
  paddingVertical: theme.spacing['16px'],
  gap: theme.spacing['8px'],
}))

const ItemList = styled.ScrollView({
  flexShrink: 1,
})

export const ProviderAgendaPanel: FunctionComponent<ProviderAgendaPanelProps> = ({
  appointments,
  isLoading,
  allCompleted = false,
}) => {
  const { colors } = useTheme()

  if (isLoading) {
    return (
      <CenteredState>
        <LoadingIndicator topPadding={0} />
        <BodyText
          size={BodyTextSize.SMALL}
          color={colors.textSecondary}
          text={<FormattedMessage defaultMessage='Loading...' description='Loading state for the agenda panel' />}
        />
      </CenteredState>
    )
  }

  if (appointments.length === 0) {
    return (
      <CenteredState testID={tID('ProviderAgendaPanel-empty')}>
        <BodyText
          size={BodyTextSize.SMALL}
          color={colors.textSecondary}
          text={
            allCompleted ? (
              <FormattedMessage
                defaultMessage="You've seen all your clients for today"
                description='Message shown when all scheduled appointments for the day are complete'
              />
            ) : (
              <FormattedMessage
                defaultMessage='No sessions scheduled'
                description='Empty state when no appointments are scheduled for the day'
              />
            )
          }
        />
      </CenteredState>
    )
  }

  return (
    <Container>
      <ItemList>
        {appointments.map((appointment, index) => (
          <ProviderAgendaItem key={appointment.id} {...appointment} isLastItem={index === appointments.length - 1} />
        ))}
      </ItemList>
    </Container>
  )
}
